import * as React from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Box, Button, useTheme } from '@mui/material';

interface SectionProps {
  sigCanvas: React.RefObject<SignatureCanvas>;
  onBegin?: () => void;
  onClear?: () => void;
  onEnd?: () => void;
  disabled?: boolean;
  alwaysShowClear?: boolean;
}

const BOTTOM_HEIGHT = '50px';

export default function SignaturePad({
  sigCanvas,
  onBegin,
  onEnd,
  onClear,
  disabled,
  alwaysShowClear,
}: SectionProps) {
  const theme = useTheme();

  const [width, setWidth] = React.useState(0);
  const [openSignature, setOpenSignature] = React.useState(false);

  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const onResize = () => {
      if (!containerRef.current) return;
      setWidth(containerRef.current!.clientWidth);
    };
    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [containerRef]);

  React.useEffect(() => {
    if (sigCanvas.current) {
      if (disabled) {
        sigCanvas.current.off();
      } else {
        sigCanvas.current.on();
      }
    }
  }, [disabled, sigCanvas]);

  const clearCanvas = () => {
    if (!openSignature) return;
    if (sigCanvas.current) {
      setOpenSignature(false);
      sigCanvas.current.clear();
      onClear?.();
    }
  };

  const onBeginDraw = () => {
    if (openSignature || disabled) return;
    onBegin?.();
    setOpenSignature(true);
  };

  const onEndDraw = () => {
    if (document.activeElement && 'blur' in document.activeElement) {
      (document.activeElement as HTMLElement).blur();
    }
    onEnd?.();
  };

  const isClearVisible = alwaysShowClear || openSignature;

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      border={`0.5px solid ${theme.palette.lightOutlineGrey.main}`}
      borderRadius="10px"
      marginBottom={!isClearVisible ? BOTTOM_HEIGHT : undefined}
      bgcolor={disabled ? theme.palette.lightGrey.main : 'transparent'}>
      {!openSignature && (
        <Box
          position="absolute"
          sx={{
            pointerEvents: 'none',
            marginBottom: isClearVisible ? BOTTOM_HEIGHT : undefined,
          }}
          color="text.tertiary">
          <span>Draw your signature here</span>
        </Box>
      )}
      <Box width="100%">
        <SignatureCanvas
          onBegin={onBeginDraw}
          onEnd={onEndDraw}
          penColor="black"
          canvasProps={{ height: '200px', width }}
          ref={sigCanvas}
        />
        {isClearVisible && (
          <Box
            height={BOTTOM_HEIGHT}
            boxSizing="border-box"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            borderTop={`0.5px solid ${theme.palette.lightOutlineGrey.main}`}>
            <Button
              disabled={!openSignature}
              variant="text"
              type="button"
              onClick={clearCanvas}
              sx={{
                border: 'none',
                marginLeft: 'auto',
                color: 'text.primary',
                padding: 0,
                display: 'block',
              }}>
              Clear
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
